"use client";

import { Application } from "@/lib/types";
import { STATUS_STEPS } from "@/lib/rules";
import { CategoryBadge } from "./CategoryBadge";
import { AlertTriangle, CheckCircle2, CloudOff, CloudUpload, Info, RefreshCw, FileCheck2, XCircle } from "lucide-react";
import { API_BASE } from "@/lib/api";

function timeAgo(ts: number) {
  const diff = Date.now() - ts;
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "1 day ago";
  return `${d} days ago`;
}

const STATUS_COLOR: Record<string, string> = {
  Draft: "#8CA0B8",
  Submitted: "#5FB6E8",
  "Under Review": "#F2A93B",
  Decided: "#35C48A",
};

export function FlightStrip({
  app,
  onAdvance,
  onRetrySync,
  onRefreshStatus,
}: {
  app: Application;
  onAdvance?: (id: string) => void;
  onRetrySync?: (id: string) => void;
  onRefreshStatus?: (id: string) => void;
}) {
  const stepIndex = STATUS_STEPS.indexOf(app.status);
  const rejected = app.decision === "rejected";
  const color = rejected ? "var(--alert)" : STATUS_COLOR[app.status];
  const blocking = app.flags.filter((f) => f.level === "block");
  const warnings = app.flags.filter((f) => f.level === "warn");
  const certUrl = app.certificateUrl ? (app.certificateUrl.startsWith("http") ? app.certificateUrl : `${API_BASE}${app.certificateUrl}`) : null;

  return (
    <div
      className="dash-in group relative flex flex-col gap-2 rounded-sm border border-l-4 bg-white px-4 py-3 transition-colors hover:border-l-[6px]"
      style={{ borderColor: "var(--line)", borderLeftColor: color }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <span className="font-mono text-[11px] text-[var(--muted-2)]">{app.id}</span>
          {app.category && <CategoryBadge category={app.category} />}
          <span className="font-display text-[15px] font-medium">{app.title}</span>
        </div>
        <span className="font-mono text-[11px]" style={{ color }}>
          {rejected ? "REJECTED" : app.status.toUpperCase()}
        </span>
      </div>

      {/* backend sync indicator — surfaces failures instead of hiding them */}
      {app.sync && app.sync !== "offline" && (
        <div className="flex items-center gap-1.5 text-[11px]">
          {app.sync === "pending" && (
            <span className="flex items-center gap-1 text-[var(--muted-2)]">
              <CloudUpload size={12} className="animate-pulse" /> Syncing to registry…
            </span>
          )}
          {app.sync === "synced" && (
            <span className="flex items-center gap-1" style={{ color: "var(--taxiway)" }}>
              <CheckCircle2 size={12} /> Saved to backend
            </span>
          )}
          {app.sync === "error" && (
            <div className="flex flex-1 items-center justify-between gap-2">
              <span className="flex items-center gap-1 text-[var(--alert)]">
                <CloudOff size={12} /> Not saved to backend{app.syncError ? ` — ${app.syncError}` : ""}
              </span>
              {onRetrySync && (
                <button
                  onClick={() => onRetrySync(app.id)}
                  className="flex items-center gap-1 rounded-sm border border-[var(--line)] px-1.5 py-0.5 text-[10px] text-[var(--muted)] hover:border-[var(--blue)] hover:text-[var(--blue)]"
                >
                  <RefreshCw size={10} /> Retry
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* progress tape — mirrors the four-stage lifecycle set out in rules 5(5)-(6) etc. */}
      <div className="flex items-center gap-1.5">
        {STATUS_STEPS.map((s, i) => (
          <div key={s} className="flex flex-1 items-center gap-1.5">
            <span
              className="h-1.5 flex-1 rounded-full"
              style={{ background: i <= stepIndex ? color : "var(--line)" }}
            />
            {i < STATUS_STEPS.length - 1 && (
              <span className="hidden font-mono text-[9px] text-[var(--muted-2)] sm:inline">·</span>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px] text-[var(--muted-2)]">
        <span className="font-mono">
          {STATUS_STEPS.map((s) => s[0]).join(" — ")}
        </span>
        <span>updated {timeAgo(app.updatedAt)}</span>
      </div>

      {app.decisionNotes && (
        <div className="rounded-sm bg-[var(--surface)] px-2.5 py-1.5 text-[12px] text-[var(--muted)]">
          <span className="text-[var(--muted-2)]">Committee note: </span>
          {app.decisionNotes}
        </div>
      )}

      {(blocking.length > 0 || warnings.length > 0) && (
        <div className="flex flex-col gap-1 border-t border-[var(--line)] pt-2">
          {blocking.map((f, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[12px] text-[var(--alert)]">
              <AlertTriangle size={13} className="mt-0.5 shrink-0" />
              <span>{f.message}</span>
            </div>
          ))}
          {warnings.map((f, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[12px] text-[var(--amber)]">
              <Info size={13} className="mt-0.5 shrink-0" />
              <span>{f.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-1 flex flex-wrap items-center gap-2">
        {certUrl && (
          <a
            href={certUrl}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-sm border border-[var(--taxiway)]/40 bg-[#0e8f5f0d] px-2 py-1 text-[11px] text-[var(--taxiway)] hover:bg-[#0e8f5f1a]"
          >
            <FileCheck2 size={12} /> Download certificate
          </a>
        )}
        {rejected && !certUrl && (
          <span className="flex items-center gap-1.5 text-[11px] text-[var(--alert)]">
            <XCircle size={12} /> Application rejected
          </span>
        )}
        {app.remoteId && onRefreshStatus && app.status !== "Decided" && (
          <button
            onClick={() => onRefreshStatus(app.id)}
            className="flex items-center gap-1 rounded-sm border border-[var(--line)] px-2 py-1 text-[11px] text-[var(--muted)] transition-colors hover:border-[var(--blue)] hover:text-[var(--blue)]"
          >
            <RefreshCw size={12} /> Check for committee update
          </button>
        )}
        {!app.remoteId && onAdvance && app.status !== "Decided" && (
          <button
            onClick={() => onAdvance(app.id)}
            className="flex items-center gap-1.5 rounded-sm border border-[var(--line)] px-2 py-1 text-[11px] text-[var(--muted)] transition-colors hover:border-[var(--blue)] hover:text-[var(--blue)]"
          >
            <CheckCircle2 size={12} /> Advance to next stage (demo)
          </button>
        )}
      </div>
    </div>
  );
}
